import { useState, useEffect, useContext, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import BusinessContext from 'context/BusinessContext';
import { normalizeUrl, isValidUrlFormat } from 'utils/url';
import { WebsiteValidationResult } from 'interfaces/WebsiteValidation';

interface UseWebsiteValidationReturn {
  isValidating: boolean;
  validationResult: WebsiteValidationResult | null;
  error: string | null;
  warning: string | null;
  isSpa: boolean;
  validateUrl: (url: string) => void;
  clearValidation: () => void;
}

export const useWebsiteValidation = (debounceMs: number = 500): UseWebsiteValidationReturn => {
  const [url, setUrl] = useState<string>('');
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<WebsiteValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  
  const [debouncedUrl] = useDebouncedValue(url, debounceMs);
  
  // Track the current request to prevent race conditions
  const currentRequestRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const {
    actions: { validateWebsite },
  } = useContext(BusinessContext);

  useEffect(() => {
    const performValidation = async () => {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!debouncedUrl) {
        setValidationResult(null);
        setError(null);
        setWarning(null);
        setIsValidating(false);
        currentRequestRef.current = null;
        return;
      }

      // Basic format check - if invalid, show error and stop
      if (!isValidUrlFormat(debouncedUrl)) {
        setError('Please enter a valid URL format');
        setValidationResult(null);
        setWarning(null);
        setIsValidating(false);
        currentRequestRef.current = null;
        return;
      }

      // If format is valid, proceed with API validation

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      
      // Track this specific request
      const requestId = debouncedUrl + Date.now();
      currentRequestRef.current = requestId;

      setIsValidating(true);
      setError(null);
      setWarning(null);

      try {
        const normalizedUrl = normalizeUrl(debouncedUrl);
        const result = await validateWebsite(normalizedUrl, abortController.signal);
        
        // Only update state if this is still the current request
        if (currentRequestRef.current === requestId && !abortController.signal.aborted) {
          setValidationResult(result);
          
          // Handle SPA detection
          if (result.is_spa) {
            setError(null);
            setWarning('Single Page Application detected - content extraction will be limited');
          } else if (!result.valid) {
            setError(result.error || 'Website is not reachable or valid');
            setWarning(null);
          } else {
            setError(null);
            setWarning(null);
          }
        }
      } catch (err: any) {
        // Only update state if this is still the current request and not aborted
        if (currentRequestRef.current === requestId && !abortController.signal.aborted) {
          console.error('Website validation failed:', err);
          setError('Failed to validate website. Please try again.');
          setValidationResult(null);
          setWarning(null);
        } else {
          // Request was cancelled - this is expected behavior
          console.log('Validation request cancelled for:', debouncedUrl, {
            requestId,
            currentRequest: currentRequestRef.current,
            aborted: abortController.signal.aborted,
            errorCode: err?.code,
            status: err?.response?.status
          });
        }
      } finally {
        // Only update loading state if this is still the current request
        if (currentRequestRef.current === requestId && !abortController.signal.aborted) {
          setIsValidating(false);
        }
      }
    };

    performValidation();
  }, [debouncedUrl, validateWebsite]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const validateUrl = (newUrl: string) => {
    setUrl(newUrl);
    if (newUrl) {
      setIsValidating(true);
    }
  };

  const clearValidation = () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setUrl('');
    setValidationResult(null);
    setError(null);
    setWarning(null);
    setIsValidating(false);
    currentRequestRef.current = null;
  };

  return {
    isValidating,
    validationResult,
    error,
    warning,
    isSpa: validationResult?.is_spa || false,
    validateUrl,
    clearValidation,
  };
}; 